import * as React from 'react';
import {
  Home,
  User,
  Settings,
  LogOut,
  Search,
  Plus,
  Edit,
  Trash2,
  Check,
  X,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Filter,
  Download,
  Upload,
  Eye,
  EyeOff,
  Mail,
  Lock,
  AlertCircle,
  CheckCircle,
  Info,
  XCircle,
  Menu,
  MoreVertical,
  Copy,
  ExternalLink,
  RefreshCw,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowDown,
  ArrowLeftRight,
  Store,
  CreditCard,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Users,
  Building,
  FileText,
  Save,
  Printer,
  Share2,
  Bell,
  HelpCircle,
  Command,
  Code,
  Link,
  BarChart3,
  MessageSquare,
  Calculator,
  Receipt,
  HandCoins,
  BookCopy,
  QrCode,
  Gift,
  Clock,
  Ticket,
  IdCard,
  Phone,
  MoreHorizontal,
  RotateCcw,
  StickyNote,
  Handshake,
  CircleDollarSign,
  AppWindow,
  ArrowLeftCircle,
  Pencil,
} from 'lucide-react';
import { LucideProps } from 'lucide-react';

export const iconRegistry = {
  home: Home,
  user: User,
  settings: Settings,
  'log-out': LogOut,
  logout: LogOut,
  search: Search,
  plus: Plus,
  add: Plus,
  edit: Edit,
  trash: Trash2,
  delete: Trash2,
  check: Check,
  x: X,
  close: X,
  'chevron-down': ChevronDown,
  'chevron-up': ChevronUp,
  'chevron-left': ChevronLeft,
  'chevron-right': ChevronRight,
  calendar: Calendar,
  filter: Filter,
  download: Download,
  upload: Upload,
  eye: Eye,
  'eye-off': EyeOff,
  mail: Mail,
  lock: Lock,
  'alert-circle': AlertCircle,
  alert: AlertCircle,
  'check-circle': CheckCircle,
  success: CheckCircle,
  info: Info,
  'x-circle': XCircle,
  error: XCircle,
  menu: Menu,
  'more-vertical': MoreVertical,
  more: MoreVertical,
  copy: Copy,
  'external-link': ExternalLink,
  refresh: RefreshCw,
  'arrow-left': ArrowLeft,
  'arrow-right': ArrowRight,
  'arrow-up': ArrowUp,
  'arrow-down': ArrowDown,
  store: Store,
  'credit-card': CreditCard,
  card: CreditCard,
  'dollar-sign': DollarSign,
  dollar: DollarSign,
  'trending-up': TrendingUp,
  'trending-down': TrendingDown,
  users: Users,
  building: Building,
  'file-text': FileText,
  file: FileText,
  save: Save,
  printer: Printer,
  print: Printer,
  share: Share2,
  bell: Bell,
  notification: Bell,
  'help-circle': HelpCircle,
  help: HelpCircle,
  command: Command,
  code: Code,
  link: Link,
  'bar-chart': BarChart3,
  analytics: BarChart3,
  'message-square': MessageSquare,
  message: MessageSquare,
  chat: MessageSquare,
  calculator: Calculator,
  receipt: Receipt,
  'hand-coins': HandCoins,
  'arrow-left-right': ArrowLeftRight,
  transactions: ArrowLeftRight,
  'book-copy': BookCopy,
  'one-app': BookCopy,
  'qr-code': QrCode,
  gift: Gift,
  clock: Clock,
  time: Clock,
  ticket: Ticket,
  'id-badge': IdCard,
  phone: Phone,
  'more-horizontal': MoreHorizontal,
  dots: MoreHorizontal,
  'rotate-ccw': RotateCcw,
  undo: RotateCcw,
  note: StickyNote,
  handshake: Handshake,
  'circle-dollar': CircleDollarSign,
  'app-window': AppWindow,
  'arrow-left-circle': ArrowLeftCircle,
  pencil: Pencil,
} as const;

export type IconName = keyof typeof iconRegistry;

export interface IconProps extends Omit<LucideProps, 'ref'> {
  /** Icon name from the registry. Use autocomplete to see available icons. */
  name: IconName;
  /** Icon size in pixels. Defaults to 20. */
  size?: number;
}

/**
 * Icon component that renders icons from the Lucide icon library using a registry pattern.
 *
 * @example
 * // Decorative icon with aria-hidden
 * <Icon name="home" size={24} aria-hidden="true" />
 *
 * @example
 * // Standalone icon with accessible label
 * <Icon name="search" size={20} aria-label="Search" />
 *
 * @example
 * // Icon with custom styling
 * <Icon name="alert" className="text-red-500" />
 */
export const Icon = React.forwardRef<SVGSVGElement, IconProps>(
  ({ name, size = 20, className, ...props }, ref) => {
    const LucideIcon = iconRegistry[name];

    if (!LucideIcon) {
      const errorMessage = `Icon "${name}" not found in registry. Available icons: ${Object.keys(
        iconRegistry
      )
        .slice(0, 10)
        .join(', ')}...`;

      // Throw error in development for fast feedback
      if (process.env.NODE_ENV === 'development') {
        throw new Error(errorMessage);
      }

      // Log error in production and fail gracefully
      console.error(errorMessage);
      return null;
    }

    return (
      <LucideIcon ref={ref} size={size} className={className} {...props} />
    );
  }
);

Icon.displayName = 'Icon';

export default Icon;
